import { useState } from 'react';
import axios from 'axios';
import { getCountries, getCountryCallingCode } from 'libphonenumber-js';
import C from '../../constants/palette';
import StoneVisual from '../shared/StoneVisual';

const STEPS = ['Buyer', 'Payment'];
const DELIVERY_FEE = 500;
const ABANDONMENT_POLICY_URL = '/terms-and-policy';
const countryNameFormatter = typeof Intl !== 'undefined' && Intl.DisplayNames
  ? new Intl.DisplayNames(['en'], { type: 'region' })
  : null;

function flagEmoji(country) {
  return country
    .toUpperCase()
    .replace(/./g, char => String.fromCodePoint(127397 + char.charCodeAt(0)));
}

function countryName(country) {
  try {
    return countryNameFormatter?.of(country) || country;
  } catch (_) {
    return country;
  }
}

const REGION_CODES = getCountries()
  .map(country => ({
    country,
    code: `+${getCountryCallingCode(country)}`,
    flag: flagEmoji(country),
    label: countryName(country),
  }))
  .sort((a, b) => a.label.localeCompare(b.label));

function formatMoney(value) {
  return `$${Number(value || 0).toLocaleString()}`;
}

export default function CheckoutPage({ stone, onClose }) {
  const [step, setStep] = useState(1);
  const [buyerName, setBuyerName] = useState('');
  const [email, setEmail] = useState('');
  const [regionCountry, setRegionCountry] = useState('');
  const [regionOpen, setRegionOpen] = useState(false);
  const [regionSearch, setRegionSearch] = useState('');
  const [contactNumber, setContactNumber] = useState('');
  const [fulfillmentType, setFulfillmentType] = useState('DELIVERY');
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [abandonmentPolicyAccepted, setAbandonmentPolicyAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  const isDelivery = fulfillmentType === 'DELIVERY';
  const deliveryFee = isDelivery ? DELIVERY_FEE : 0;
  const total = Number(stone.price || 0) + deliveryFee;
  const selectedRegion = REGION_CODES.find(region => region.country === regionCountry);
  const fullContactNumber = `${selectedRegion?.code || ''} ${contactNumber.trim()}`.trim();
  const regionQuery = regionSearch.trim().toLowerCase();
  const visibleRegions = REGION_CODES.filter(region => {
    if (!regionQuery) return true;
    return [
      region.label,
      region.country,
      region.code,
    ].some(value => String(value).toLowerCase().includes(regionQuery));
  });

  const inputStyle = {
    width: '100%',
    padding: '13px 0',
    border: 'none',
    borderBottom: `1.5px solid ${C.border}`,
    fontSize: 15,
    color: C.ink,
    outline: 'none',
    background: 'transparent',
    fontFamily: 'DM Sans',
    transition: 'border-color 0.2s',
  };

  const labelStyle = {
    fontSize: 10,
    letterSpacing: '0.18em',
    color: C.muted,
    fontWeight: 600,
    display: 'block',
    marginBottom: 10,
  };

  const handleFocus = e => { e.target.style.borderBottomColor = C.gold; };
  const handleBlur = e => { e.target.style.borderBottomColor = C.border; };

  const startPayment = async () => {
    setErr('');
    setLoading(true);
    try {
      const res = await axios.post('/api/payments/create', {
        treasureId: stone.id,
        email,
        buyerName,
        contactNumber: fullContactNumber,
        fulfillmentType,
        deliveryAddress: isDelivery ? deliveryAddress : '',
        abandonmentPolicyAccepted: !isDelivery && abandonmentPolicyAccepted,
      });
      if (!res.data.checkoutUrl) {
        throw new Error('Payment gateway did not return a checkout URL.');
      }
      window.location.href = res.data.checkoutUrl;
    } catch (error) {
      setErr(error.response?.data?.error || error.message || 'Unable to start payment. Please try again.');
      setLoading(false);
    }
  };

  const submitDetails = (e) => {
    e.preventDefault();
    setErr('');
    if (!selectedRegion) {
      setErr('Please select a phone region code.');
      return;
    }
    if (!isDelivery && !abandonmentPolicyAccepted) {
      setErr('Please acknowledge the abandonment policy before continuing.');
      return;
    }
    setStep(2);
  };

  return (
    <div style={{ minHeight: '100vh', background: '#F8F9FA', display: 'flex', flexDirection: 'column' }}>
      <header style={{
        height: 60,
        flexShrink: 0,
        background: '#fff',
        borderBottom: `1px solid ${C.border}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 48px',
      }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
          <span style={{ fontFamily: 'Playfair Display', fontSize: 20, fontWeight: 500, color: C.ink, letterSpacing: '0.04em' }}>
            KINGSTRADE
          </span>
          <span style={{ fontSize: 9, color: C.muted, letterSpacing: '0.18em', fontWeight: 500 }}>
            SECURE ACQUISITION
          </span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center' }}>
          {STEPS.map((s, i) => {
            const num = i + 1;
            const done = step > num;
            const active = step === num;
            return (
              <span key={s} style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    background: done ? C.gold : active ? C.ink : C.border,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 9,
                    fontWeight: 700,
                    color: done || active ? '#fff' : C.muted,
                    fontFamily: 'DM Mono',
                  }}>
                    {done ? '✓' : num}
                  </span>
                  <span style={{
                    fontSize: 10,
                    letterSpacing: '0.12em',
                    fontWeight: 600,
                    color: active ? C.ink : done ? C.gold : C.muted,
                  }}>
                    {s.toUpperCase()}
                  </span>
                </span>
                {i < STEPS.length - 1 && (
                  <span style={{ width: 32, height: 1, background: done ? C.gold : C.border, margin: '0 8px', display: 'inline-block' }} />
                )}
              </span>
            );
          })}
        </div>

        <button onClick={onClose} style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          fontSize: 11,
          color: C.muted,
          letterSpacing: '0.12em',
        }}>
          ← BACK TO COLLECTION
        </button>
      </header>

      <div style={{ flex: 1, display: 'flex', maxWidth: 1100, margin: '0 auto', width: '100%', padding: '0 32px' }}>
        <div style={{
          width: 340,
          flexShrink: 0,
          padding: '64px 48px 64px 0',
          borderRight: `1px solid ${C.border}`,
        }}>
          <div style={{ aspectRatio: '1/1', background: stone.bg, overflow: 'hidden', marginBottom: 28 }}>
            <StoneVisual stone={stone} size={300} />
          </div>
          <p style={{ fontSize: 9, color: C.gold, letterSpacing: '0.2em', fontWeight: 600, marginBottom: 8 }}>
            {stone.author || 'UNKNOWN AUTHOR'} · {stone.id}
          </p>
          <p style={{ fontFamily: 'Playfair Display', fontSize: 20, color: C.ink, marginBottom: 20, lineHeight: 1.3 }}>
            {stone.name}
          </p>

          <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 20 }}>
            {[
              ['Author', stone.author || 'Unknown'],
              ['Treasure ID', stone.id],
              ['Status', stone.status],
              ['Collection', 'Kingstrade archive'],
            ].map(([k, v]) => (
              <div key={k} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
                <span style={{ fontSize: 11, color: C.muted }}>{k}</span>
                <span style={{ fontSize: 11, color: C.ink, fontWeight: 500, textAlign: 'right' }}>{v}</span>
              </div>
            ))}
            <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 16, marginTop: 4 }}>
              <SummaryLine label="Treasure" value={formatMoney(stone.price)} />
              <SummaryLine label={isDelivery ? 'Delivery fee' : 'Pickup fee'} value={formatMoney(deliveryFee)} muted={!isDelivery} />
              <SummaryLine label="Total" value={formatMoney(total)} strong />
            </div>
          </div>

          <div style={{ marginTop: 28, padding: '14px 16px', background: '#f0f4ff', display: 'flex', gap: 10, alignItems: 'flex-start' }}>
            <div>
              <p style={{ fontSize: 10, fontWeight: 600, color: '#3b5bdb', letterSpacing: '0.1em', marginBottom: 3 }}>
                SECURE PAYMENT
              </p>
              <p style={{ fontSize: 11, color: C.muted, lineHeight: 1.5 }}>
                Delivery adds a fixed USD 500 fee. The purchase terms include Kingstrade's abandonment and uncollected items policy.
              </p>
              <a
                href={ABANDONMENT_POLICY_URL}
                target="_blank"
                rel="noreferrer"
                style={{ display: 'inline-block', marginTop: 9, color: '#3b5bdb', fontSize: 11, fontWeight: 700, textDecoration: 'underline' }}
              >
                Terms &amp; Conditions: read the policy ↗
              </a>
            </div>
          </div>
        </div>

        <div style={{ flex: 1, padding: '64px 0 64px 64px', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start' }}>
          {step === 1 && (
            <div>
              <p style={{ fontSize: 9, color: C.gold, letterSpacing: '0.22em', fontWeight: 600, marginBottom: 16 }}>
                STEP 1 OF 2 - BUYER DETAILS
              </p>
              <h2 style={{ fontFamily: 'Playfair Display', fontSize: 36, fontWeight: 400, color: C.ink, marginBottom: 10 }}>
                Contact and Fulfillment
              </h2>
              <p style={{ fontSize: 13, color: C.muted, marginBottom: 36, lineHeight: 1.7, fontWeight: 300, maxWidth: 520 }}>
                Provide the buyer details and choose delivery or self pickup before continuing to payment.
              </p>
              <form onSubmit={submitDetails}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, marginBottom: 28 }}>
                  <Field label="Buyer Name" labelStyle={labelStyle}>
                    <input value={buyerName} onChange={e => setBuyerName(e.target.value)} required placeholder="Full name" style={inputStyle} onFocus={handleFocus} onBlur={handleBlur} />
                  </Field>
                  <Field label="Contact Number" labelStyle={labelStyle}>
                    <div style={{ display: 'grid', gridTemplateColumns: '92px 1fr', gap: 12, alignItems: 'end', position: 'relative' }}>
                      <button
                        type="button"
                        onClick={() => setRegionOpen(open => !open)}
                        style={{
                          height: 48,
                          border: 'none',
                          borderBottom: `1.5px solid ${regionOpen ? C.gold : C.border}`,
                          background: 'transparent',
                          cursor: 'pointer',
                          color: C.ink,
                          fontFamily: 'DM Sans',
                          fontSize: 15,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 8,
                          padding: '13px 0',
                        }}
                        aria-label="Select contact number region code"
                      >
                        <span style={{ fontSize: 18, lineHeight: 1 }}>{selectedRegion?.flag || '🌐'}</span>
                        <span style={{ color: C.muted, fontSize: 11 }}>▾</span>
                      </button>
                      <input value={contactNumber} onChange={e => setContactNumber(e.target.value)} required placeholder="Phone number" style={inputStyle} onFocus={handleFocus} onBlur={handleBlur} />
                      {regionOpen && (
                        <div style={{
                          position: 'absolute',
                          top: 54,
                          left: 0,
                          width: 360,
                          maxWidth: 'min(360px, 90vw)',
                          background: '#fff',
                          border: `1px solid ${C.border}`,
                          boxShadow: '0 18px 48px rgba(15,23,42,0.14)',
                          zIndex: 50,
                        }}>
                          <div style={{ padding: 10, borderBottom: `1px solid ${C.border}` }}>
                            <input
                              value={regionSearch}
                              onChange={e => setRegionSearch(e.target.value)}
                              placeholder="Search country or code"
                              autoFocus
                              style={{
                                width: '100%',
                                border: `1px solid ${C.border}`,
                                padding: '10px 12px',
                                outline: 'none',
                                fontSize: 13,
                                color: C.ink,
                                fontFamily: 'DM Sans',
                              }}
                            />
                          </div>
                          <div style={{ maxHeight: 300, overflow: 'auto' }}>
                            {visibleRegions.map(region => (
                              <button
                                key={region.country}
                                type="button"
                                onClick={() => {
                                  setRegionCountry(region.country);
                                  setRegionSearch('');
                                  setRegionOpen(false);
                                }}
                                style={{
                                  width: '100%',
                                  border: 'none',
                                  borderBottom: `1px solid ${C.border}`,
                                  background: region.country === regionCountry ? 'rgba(212,175,55,0.1)' : '#fff',
                                  cursor: 'pointer',
                                  display: 'grid',
                                  gridTemplateColumns: '34px 1fr auto',
                                  gap: 10,
                                  alignItems: 'center',
                                  padding: '11px 12px',
                                  textAlign: 'left',
                                  color: C.ink,
                                  fontFamily: 'DM Sans',
                                }}
                              >
                                <span style={{ fontSize: 18 }}>{region.flag}</span>
                                <span style={{ fontSize: 13 }}>{region.label}</span>
                                <span style={{ fontFamily: 'DM Mono', fontSize: 12, color: C.muted }}>{region.code}</span>
                              </button>
                            ))}
                            {visibleRegions.length === 0 && (
                              <p style={{ padding: 16, color: C.muted, fontSize: 12 }}>No matching region</p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </Field>
                  <div style={{ gridColumn: 'span 2' }}>
                    <Field label="Email Address" labelStyle={labelStyle}>
                      <input type="email" value={email} onChange={e => setEmail(e.target.value)} required placeholder="your@email.com" style={inputStyle} onFocus={handleFocus} onBlur={handleBlur} />
                    </Field>
                  </div>
                </div>

                <label style={labelStyle}>DELIVERY OPTION</label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 28 }}>
                  <OptionButton
                    active={isDelivery}
                    title="Delivery"
                    subtitle="Fixed USD 500 fee"
                    onClick={() => setFulfillmentType('DELIVERY')}
                  />
                  <OptionButton
                    active={!isDelivery}
                    title="Self Pickup"
                    subtitle="Policy acknowledgement required"
                    onClick={() => setFulfillmentType('SELF_PICKUP')}
                  />
                </div>

                {isDelivery ? (
                  <Field label="Delivery Address" labelStyle={labelStyle}>
                    <textarea
                      value={deliveryAddress}
                      onChange={e => setDeliveryAddress(e.target.value)}
                      required
                      rows={4}
                      placeholder="Street, building, district, city, postal code"
                      style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.5 }}
                      onFocus={handleFocus}
                      onBlur={handleBlur}
                    />
                  </Field>
                ) : (
                  <label style={{
                    display: 'flex',
                    gap: 12,
                    alignItems: 'flex-start',
                    border: `1px solid ${C.border}`,
                    background: '#fff',
                    padding: 16,
                    marginBottom: 24,
                    cursor: 'pointer',
                  }}>
                    <input
                      type="checkbox"
                      checked={abandonmentPolicyAccepted}
                      onChange={e => setAbandonmentPolicyAccepted(e.target.checked)}
                      required
                      style={{ marginTop: 2 }}
                    />
                    <span>
                      <span style={{ display: 'block', color: C.ink, fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', marginBottom: 5 }}>
                        ABANDONMENT POLICY ACKNOWLEDGEMENT
                      </span>
                      <span style={{ display: 'block', color: C.muted, fontSize: 12, lineHeight: 1.6 }}>
                        I acknowledge that the self-pickup abandonment policy applies to this purchase.{' '}
                        <a
                          href={ABANDONMENT_POLICY_URL}
                          target="_blank"
                          rel="noreferrer"
                          onClick={e => e.stopPropagation()}
                          style={{ color: '#3b5bdb', fontWeight: 700, textDecoration: 'underline' }}
                        >
                          Read the Terms &amp; Conditions policy
                        </a>
                        .
                      </span>
                    </span>
                  </label>
                )}

                {err && <p style={{ fontSize: 12, color: '#e03131', marginBottom: 18 }}>{err}</p>}
                <div style={{ marginTop: 40 }}>
                  <button type="submit" style={{
                    padding: '16px 48px',
                    background: C.gold,
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: 11,
                    letterSpacing: '0.2em',
                    fontWeight: 600,
                    color: C.dark,
                  }}>
                    CONTINUE TO PAYMENT →
                  </button>
                </div>
              </form>
            </div>
          )}

          {step === 2 && (
            <div>
              <p style={{ fontSize: 9, color: C.gold, letterSpacing: '0.22em', fontWeight: 600, marginBottom: 16 }}>
                STEP 2 OF 2 - PAYMENT
              </p>
              <h2 style={{ fontFamily: 'Playfair Display', fontSize: 36, fontWeight: 400, color: C.ink, marginBottom: 10 }}>
                Secure Payment
              </h2>
              <p style={{ fontSize: 13, color: C.muted, marginBottom: 36, lineHeight: 1.7, fontWeight: 300, maxWidth: 440 }}>
                Continue to the secure payment page. The treasure is marked sold only after the payment gateway confirms completion.
              </p>
              <form onSubmit={async e => {
                e.preventDefault();
                await startPayment();
              }}>
                <div style={{
                  border: `1px solid ${C.border}`,
                  background: '#fff',
                  padding: '22px 24px',
                  marginBottom: 32,
                }}>
                  <p style={{ fontSize: 10, letterSpacing: '0.16em', color: C.muted, fontWeight: 700, marginBottom: 16 }}>
                    PAYMENT SUMMARY
                  </p>
                  <SummaryLine label={stone.name} value={formatMoney(stone.price)} />
                  <SummaryLine label={isDelivery ? 'Delivery fee' : 'Self pickup'} value={formatMoney(deliveryFee)} muted={!isDelivery} />
                  <div style={{ height: 1, background: C.border, margin: '14px 0' }} />
                  <SummaryLine label="Total due" value={formatMoney(total)} strong />
                  <div style={{ borderTop: `1px solid ${C.border}`, marginTop: 18, paddingTop: 16 }}>
                    <p style={{ fontSize: 11, color: C.muted, lineHeight: 1.7 }}>
                      Buyer: <strong style={{ color: C.ink }}>{buyerName}</strong><br />
                      Receipt email: <strong style={{ color: C.ink }}>{email}</strong><br />
                      Contact: <strong style={{ color: C.ink }}>{fullContactNumber}</strong><br />
                      Fulfillment: <strong style={{ color: C.ink }}>{isDelivery ? 'Delivery' : 'Self pickup'}</strong>
                    </p>
                    <p style={{ fontSize: 11, color: C.muted, lineHeight: 1.6, marginTop: 12 }}>
                      By proceeding, you agree to the Kingstrade purchase terms, including the{' '}
                      <a
                        href={ABANDONMENT_POLICY_URL}
                        target="_blank"
                        rel="noreferrer"
                        style={{ color: '#3b5bdb', fontWeight: 700, textDecoration: 'underline' }}
                      >
                        Abandonment and Uncollected Items Policy
                      </a>
                      .
                    </p>
                  </div>
                </div>
                {err && <p style={{ fontSize: 12, color: '#e03131', marginBottom: 18 }}>{err}</p>}
                <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                  <button type="submit" disabled={loading} style={{
                    padding: '18px 48px',
                    background: loading ? '#e8d080' : C.gold,
                    border: 'none',
                    cursor: loading ? 'default' : 'pointer',
                    fontSize: 12,
                    letterSpacing: '0.2em',
                    fontWeight: 700,
                    color: C.dark,
                  }}>
                    {loading ? 'STARTING PAYMENT...' : 'PROCEED TO SECURE PAYMENT →'}
                  </button>
                  <button type="button" onClick={() => setStep(1)} style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: 11,
                    color: C.muted,
                    letterSpacing: '0.1em',
                  }}>
                    ← Back
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, labelStyle, children }) {
  return (
    <label style={{ display: 'block' }}>
      <span style={labelStyle}>{label.toUpperCase()}</span>
      {children}
    </label>
  );
}

function OptionButton({ active, title, subtitle, onClick }) {
  return (
    <button type="button" onClick={onClick} style={{
      textAlign: 'left',
      border: active ? `1.5px solid ${C.gold}` : `1px solid ${C.border}`,
      background: active ? 'rgba(212,175,55,0.08)' : '#fff',
      padding: '16px 18px',
      cursor: 'pointer',
      minHeight: 84,
    }}>
      <span style={{ display: 'block', color: active ? C.ink : C.muted, fontSize: 12, fontWeight: 700, letterSpacing: '0.1em', marginBottom: 8 }}>
        {title.toUpperCase()}
      </span>
      <span style={{ display: 'block', color: C.muted, fontSize: 12, lineHeight: 1.45 }}>
        {subtitle}
      </span>
    </button>
  );
}

function SummaryLine({ label, value, strong, muted }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 24, alignItems: 'baseline', marginBottom: strong ? 0 : 10 }}>
      <span style={{ fontSize: strong ? 13 : 12, fontWeight: strong ? 700 : 400, color: muted ? C.muted : C.ink }}>
        {label}
      </span>
      <span style={{ fontFamily: 'DM Mono', fontSize: strong ? 18 : 14, color: muted ? C.muted : C.ink, fontWeight: strong ? 700 : 500 }}>
        {value}
      </span>
    </div>
  );
}
